import {
  CamAction,
  CurrentAndSiblingEscalations,
  Escalation,
  EscalationState,
  GridConfig,
  UnifiedMonitoringMessageType,
} from '@hakimo-ui/hakimo/types';
import { EscalationScope, partitionEscalations } from '@hakimo-ui/hakimo/util';

export function calculateGridConfig(itemCount: number): GridConfig {
  if (itemCount <= 0) return { rows: 0, cols: 0 };
  if (itemCount === 1) return { rows: 1, cols: 1 };

  // Browser viewport assumptions
  const viewportRatio = window.innerWidth / window.innerHeight;
  const videoRatio = 16 / 9;

  // Start with square-ish grid and adjust based on viewport
  let bestColumns = Math.ceil(Math.sqrt(itemCount));
  let bestScore = -1;

  // Test different column counts around the square root
  const maxColumns = Math.min(itemCount, Math.ceil(Math.sqrt(itemCount) * 2));

  for (let cols = 1; cols <= maxColumns; cols++) {
    const rows = Math.ceil(itemCount / cols);

    // Calculate how well this grid fits the viewport
    const gridRatio = (cols * videoRatio) / rows;
    const ratioScore = Math.min(
      gridRatio / viewportRatio,
      viewportRatio / gridRatio
    );

    // Prefer fewer empty slots
    const utilization = itemCount / (cols * rows);

    const score = ratioScore * 0.7 + utilization * 0.3;

    if (score > bestScore) {
      bestScore = score;
      bestColumns = cols;
    }
  }

  return {
    cols: bestColumns,
    rows: Math.ceil(itemCount / bestColumns),
  };
}

export function getEventTypeFromCamAction(
  actionType: CamAction
): UnifiedMonitoringMessageType {
  switch (actionType) {
    case CamAction.ESCALATE:
      return UnifiedMonitoringMessageType.ALARM_ESCALATION_OPEN;
    case CamAction.SAFE:
      return UnifiedMonitoringMessageType.ALARM_SAFE;
    case CamAction.RESOLVE_ESCALATION:
      return UnifiedMonitoringMessageType.ALARM_ESCALATION_CLOSE;
    default:
      return UnifiedMonitoringMessageType.ALARM_SAFE;
  }
}

/**
 * Splits the location's escalations into the one this alarm group owns and the
 * rest, which render as read-only sibling banners.
 *
 * The escalations arrive on the unified-alarm-group message for the whole
 * location, so "mine vs theirs" has to be decided client-side. The rule itself
 * lives in `partitionEscalations` / `isEscalationOwnedBy` so this path and the
 * one in AlarmGroupContainer can be reconciled; all this adds is the mapping to
 * Unified Monitoring's own EscalationState shape. The group id travels in
 * `scope.alarmGroupId`, which the ownership fallback needs anyway.
 */
export function getCurrentAndSiblingEscalations(
  escalations: Escalation[],
  scope: EscalationScope = {}
): CurrentAndSiblingEscalations {
  const toState = (escalation: Escalation): EscalationState => ({
    alarmGroupId: escalation.initiatingLocationAlarmId,
    escalationId: escalation.id,
    comment: escalation.description,
    escalationOpenTimestamp: new Date(
      escalation.incidentStartTimeUtc
    ).getTime(),
  });

  const { active, siblings } = partitionEscalations(escalations, scope);

  return {
    currentAlarmEscalation: active ? toState(active) : undefined,
    siblingEscalations: siblings.map(toState),
  };
}
